import React, { useRef, useLayoutEffect, useState } from "react"
import { useScrollDirection } from "hooks/useScrollDirection"

interface ScrollHideContainerProps {
  /**
   * The content to be shown/hidden based on scroll
   */
  children: React.ReactNode
  /**
   * Additional CSS classes to apply to the container
   */
  className?: string
  /**
   * Threshold in pixels before hiding content (fallback if no triggerOffset is computed)
   */
  threshold?: number
  /**
   * Delay in ms before updating visibility
   */
  delay?: number
  /**
   * If you want to wait until the element is 56px from the top (AppBar height),
   * you can override it here. Otherwise, 0 means “exactly the element’s top.”
   */
  hideOffset?: number
}

/**
 * A container component that shows/hides its content based on scroll direction.
 * It uses a ref and a layout effect to determine its original position on the page,
 * so that the hide effect only happens after scrolling past the element’s initial area.
 */
const ScrollHideContainer: React.FC<ScrollHideContainerProps> = ({
  children,
  className = "",
  threshold = 100,
  delay = 50,
  hideOffset = 56 // Default to 56 px if you want to account for your AppBar
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [triggerOffset, setTriggerOffset] = useState<number>(threshold)

  // Compute the element’s original position relative to the document.
  useLayoutEffect(() => {
    if (containerRef.current) {
      const offset = containerRef.current.getBoundingClientRect().top + window.scrollY + 96
      // Subtract the "AppBar" height or the offset you desire.
      setTriggerOffset(offset - hideOffset)
    }
  }, [hideOffset])

  const { isVisible } = useScrollDirection({ threshold, delay, triggerOffset })

  return (
    <div
      ref={containerRef}
      className={`transform transition-all duration-300 ease-in-out ${
        (containerRef.current && containerRef.current?.getBoundingClientRect().top < hideOffset) && !isVisible ? "-translate-y-full opacity-0 pointer-events-none" : `-translate-y-0 sticky top-[${threshold}px]`
      } ${className}`}
      style={{ willChange: "transform, opacity" }}
    >
      {children}
    </div>
  )
}

export default ScrollHideContainer
